import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { useSettings } from '../../contexts/SettingsContext';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import {
  TrashIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  UserPlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BellAlertIcon,
  PencilIcon,
  XMarkIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

interface EditState {
  full_name: string;
  role: string;
  new_password: string;
}

export default function UserManagement() {
  const { state: settingsState } = useSettings();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editState, setEditState] = useState<EditState>({ full_name: '', role: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);

  useEffect(() => {
    fetchCurrentUser();
    fetchUsers();
  }, []);

  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    return {
      'Authorization': `Bearer ${session?.access_token || anonKey}`,
      'Apikey': anonKey,
    };
  };

  const edgeFnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-admin`;

  const fetchCurrentUser = async () => {
    const { data } = await supabase.auth.getUser();
    if (data.user) setCurrentUserId(data.user.id);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(edgeFnUrl, { method: 'GET', headers });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Failed to load users');
      setUsers(result.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(edgeFnUrl, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Failed to send invite');
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail('');
      fetchUsers();
    } catch (err: any) {
      toast.error(err.message || 'Failed to send invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResendInvite = async (user: AdminUser) => {
    setResendingId(user.id);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(edgeFnUrl, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Failed to resend invite');
      toast.success(`Invite resent to ${user.email}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to resend invite');
    } finally {
      setResendingId(null);
    }
  };

  const openEdit = (user: AdminUser) => {
    setEditingUser(user);
    setEditState({ full_name: user.full_name || '', role: user.role, new_password: '' });
    setShowPassword(false);
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    if (editState.new_password && editState.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setEditSaving(true);
    try {
      const headers = await getAuthHeaders();
      const payload: Record<string, unknown> = { id: editingUser.id, full_name: editState.full_name, role: editState.role };
      if (editState.new_password.trim()) payload.new_password = editState.new_password.trim();
      const response = await fetch(edgeFnUrl, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Failed to update user');
      setUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, ...editState } : u));
      toast.success('User updated');
      setEditingUser(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update user');
    } finally {
      setEditSaving(false);
    }
  };

  const handleToggleActive = async (userId: string, currentActive: boolean) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(edgeFnUrl, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: userId, is_active: !currentActive }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Failed to update status');
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !currentActive } : u));
      toast.success(currentActive ? 'User deactivated' : 'User activated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update user status');
    }
  };

  const handleDeleteClick = (user: AdminUser) => {
    if (user.id === currentUserId) {
      toast.error('You cannot delete your own account');
      return;
    }
    setDeleteTarget(user);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(edgeFnUrl, {
        method: 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: deleteTarget.id }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Failed to remove user');
      setUsers(prev => prev.filter(u => u.id !== deleteTarget.id));
      toast.success('User removed successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to remove user');
    } finally {
      setDeletingId(null);
      setDeleteTarget(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const roleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-golf-orange/10 text-golf-orange';
      case 'editor': return 'bg-blue-100 text-blue-700';
      case 'viewer': return 'bg-teal-100 text-teal-700';
      case 'scorer': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const pendingUsers = users.filter(u => !u.is_active);
  const activeUsers = users.filter(u => u.is_active);

  const renderUserRow = (user: AdminUser) => (
    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-golf-orange/10 flex items-center justify-center flex-shrink-0">
            <EnvelopeIcon className="w-4 h-4 text-golf-orange" />
          </div>
          <div>
            {user.full_name && (
              <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
            )}
            <div className={`text-sm ${user.full_name ? 'text-gray-500' : 'font-medium text-gray-900'}`}>{user.email}</div>
            {user.id === currentUserId && (
              <div className="text-xs text-gray-400">You</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {user.is_active ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">
            <CheckCircleIcon className="w-3 h-3" /> Active
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-700">
            <ClockIcon className="w-3 h-3" /> Pending
          </span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${roleBadgeClass(user.role)}`}>
          {user.role}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(user.created_at)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="flex items-center justify-end gap-1">
          {!user.is_active && (
            <button
              onClick={() => handleResendInvite(user)}
              disabled={resendingId === user.id}
              className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-40"
              title="Resend invite"
            >
              <EnvelopeIcon className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => openEdit(user)}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            title="Edit user"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          {user.id !== currentUserId && (
            <button
              onClick={() => handleToggleActive(user.id, user.is_active)}
              className={`p-2 rounded-lg transition-colors ${user.is_active ? 'text-amber-500 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}`}
              title={user.is_active ? 'Deactivate user' : 'Activate user'}
            >
              {user.is_active ? <XCircleIcon className="w-4 h-4" /> : <CheckCircleIcon className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={() => handleDeleteClick(user)}
            disabled={deletingId === user.id || user.id === currentUserId}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={user.id === currentUserId ? 'Cannot delete your own account' : 'Remove user'}
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );

  const tableHeader = (
    <thead className="bg-gray-50">
      <tr>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
      </tr>
    </thead>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-golf-gray">User Management</h2>
        <button
          onClick={fetchUsers}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-golf-gray transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-base font-medium text-golf-gray mb-4 flex items-center gap-2">
          <UserPlusIcon className="w-5 h-5 text-golf-orange" />
          Invite Admin User
        </h3>
        <form onSubmit={handleInvite} className="flex gap-3">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="admin@example.com"
            className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
            disabled={inviteLoading}
            required
          />
          <button
            type="submit"
            disabled={inviteLoading}
            className="px-4 py-2 bg-golf-orange text-white rounded-lg text-sm font-medium hover:bg-golf-orange/90 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {inviteLoading ? 'Sending...' : 'Send Invite'}
          </button>
        </form>
        <p className="mt-2 text-xs text-gray-500">
          The user will receive a password setup email to access the admin dashboard.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-golf-orange border-t-transparent"></div>
        </div>
      ) : (
        <>
          {pendingUsers.length > 0 && (
            <div className="bg-amber-50 rounded-xl border border-amber-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-amber-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BellAlertIcon className="w-5 h-5 text-amber-600" />
                  <h3 className="text-base font-semibold text-amber-800">Pending Approval</h3>
                  <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-xs font-bold">
                    {pendingUsers.length}
                  </span>
                </div>
                <p className="text-xs text-amber-700">Awaiting access. Approve or resend their invite.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-amber-200">
                  {tableHeader}
                  <tbody className="bg-white divide-y divide-amber-100">
                    {pendingUsers.map(renderUserRow)}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeUsers.length === 0 && pendingUsers.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <ShieldCheckIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No admin users found.</p>
            </div>
          ) : activeUsers.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                <ShieldCheckIcon className="w-5 h-5 text-gray-400" />
                <h3 className="text-base font-semibold text-golf-gray">Active Users</h3>
                <span className="ml-1 text-xs text-gray-400">({activeUsers.length})</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  {tableHeader}
                  <tbody className="bg-white divide-y divide-gray-200">
                    {activeUsers.map(renderUserRow)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-golf-gray">Edit User</h3>
              <button onClick={() => setEditingUser(null)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <p className="text-xs text-gray-400 mb-1">Email</p>
                <p className="text-sm font-medium text-gray-700">{editingUser.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={editState.full_name}
                  onChange={(e) => setEditState(s => ({ ...s, full_name: e.target.value }))}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password <span className="text-xs text-gray-400 font-normal">(leave blank to keep current)</span>
                </label>
                <div className="relative">
                  <KeyIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={editState.new_password}
                    onChange={(e) => setEditState(s => ({ ...s, new_password: e.target.value }))}
                    className="w-full pl-9 pr-10 rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                    placeholder="Enter new password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
                {editState.new_password && editState.new_password.length < 8 && (
                  <p className="mt-1 text-xs text-red-500">Password must be at least 8 characters</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={editState.role}
                  onChange={(e) => setEditState(s => ({ ...s, role: e.target.value }))}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                >
                  {settingsState.userRoles.length > 0 ? (
                    settingsState.userRoles.map((r) => (
                      <option key={r.id} value={r.name.toLowerCase()}>{r.name}</option>
                    ))
                  ) : (
                    <>
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                      <option value="scorer">Scorer</option>
                      <option value="user">User</option>
                    </>
                  )}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setEditingUser(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={editSaving}
                className="px-4 py-2 bg-golf-orange text-white rounded-lg text-sm font-medium hover:bg-golf-orange/90 transition-colors disabled:opacity-50"
              >
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Remove User"
        message={deleteTarget ? `Remove ${deleteTarget.full_name || deleteTarget.email} from the system? This cannot be undone.` : ''}
      />
    </div>
  );
}
